import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { useToast } from '@/hooks/use-toast';

interface AttendanceExportButtonsProps {
  /** Fetches every row of the current filter and downloads it in `format`. */
  onExport: (format: 'csv' | 'pdf') => Promise<void>;
}

/** CSV and PDF download of the Manage Attendance list (ATTENDANCE.EXPORT). */
export const AttendanceExportButtons = ({ onExport }: AttendanceExportButtonsProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [busy, setBusy] = useState<'csv' | 'pdf' | null>(null);

  const run = async (format: 'csv' | 'pdf') => {
    setBusy(format);
    try {
      await onExport(format);
      toast({ title: t('DOWNLOAD_SUCCESSFUL') });
    } catch {
      toast({ variant: 'destructive', title: t('DOWNLOAD_FAILED') });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex gap-2">
      {(['csv', 'pdf'] as const).map((format) => (
        <Button
          key={format}
          variant="outline"
          onClick={() => run(format)}
          loading={busy === format}
          disabled={busy !== null}
        >
          <Download className="size-4" />
          {/* On a phone just the format, so both buttons fit beside the filters. */}
          <span className="max-md:hidden">{t(format === 'csv' ? 'EXPORT_CSV' : 'EXPORT_PDF')}</span>
          <span className="md:hidden">{format.toUpperCase()}</span>
        </Button>
      ))}
    </div>
  );
};
